import React, { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptingBankPicker = ({ onPick }) => {
  const [banks, setBanks] = useState([]);
  useEffect(() => {
    axios.get(`${API}/counterparties`).then((r) => setBanks(r.data.counterparties || [])).catch(() => {});
  }, []);
  if (!banks.length) return null;
  return (
    <div className="mb-3">
      <label className="field-label">Quick-fill from saved banks / counterparties</label>
      <select
        className="field-input"
        defaultValue=""
        data-testid="accepting-bank-picker"
        onChange={(e) => {
          const cp = banks.find((b) => b.id === e.target.value);
          if (cp) onPick(cp);
          e.target.value = "";
        }}
      >
        <option value="">— select a saved bank to auto-fill —</option>
        {banks.map((b) => (
          <option key={b.id} value={b.id}>{b.bank_name || b.name}{b.contact_name ? ` · ${b.contact_name}` : ""}</option>
        ))}
      </select>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", placeholder = "", testid }) => (
  <div>
    <label className="field-label" htmlFor={`f-${name}`}>{label}</label>
    <input
      id={`f-${name}`}
      className="field-input"
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(name, type === "number" ? Number(e.target.value) : e.target.value)}
      data-testid={testid || `field-${name}`}
    />
  </div>
);

const TextArea = ({ label, name, value, onChange, testid }) => (
  <div>
    <label className="field-label" htmlFor={`f-${name}`}>{label}</label>
    <textarea
      id={`f-${name}`}
      className="field-textarea"
      value={value ?? ""}
      onChange={(e) => onChange(name, e.target.value)}
      data-testid={testid || `field-${name}`}
    />
  </div>
);

export const STEPS = [
  { id: "deal", label: "Deal Snapshot" },
  { id: "trust", label: "SPV Trust" },
  { id: "borrower", label: "Originator / Settlor" },
  { id: "lender", label: "Lender (Financing)" },
  { id: "maker", label: "Maker · Treasury · Acceptance" },
  { id: "buyer", label: "Buyer (Accepting Bank/Fund)" },
  { id: "proceeds", label: "Proceeds Direction" },
  { id: "terms", label: "Financial Terms" },
  { id: "dates", label: "Dates & Law" },
];

export default function IntakeForm({ d, setField, step }) {
  const onChange = (name, val) => setField(name, val);

  if (step === "deal") {
    return (
      <div className="space-y-5 fade-enter">
        <p className="text-sm text-[#5C5C5C] leading-relaxed">
          A deal snapshot summarizes the underlying instrument and the financing/sale structure.
          All six documents are generated from a single source of truth.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Pledged / Subject Note Face Value" name="faceValue" type="number" value={d.faceValue} onChange={onChange} />
          <Field label="Coupon Rate (% annual)" name="couponRate" type="number" value={d.couponRate} onChange={onChange} />
          <Field label="Term (days)" name="termDays" type="number" value={d.termDays} onChange={onChange} />
          <Field label="Loan Principal" name="loanPrincipal" type="number" value={d.loanPrincipal} onChange={onChange} />
          <Field label="Loan Rate (% annual)" name="loanRate" type="number" value={d.loanRate} onChange={onChange} />
          <Field label="Discount Sale Price" name="purchasePrice" type="number" value={d.purchasePrice} onChange={onChange} />
        </div>
      </div>
    );
  }

  if (step === "trust") {
    return (
      <div className="space-y-4 fade-enter">
        <p className="text-sm text-[#5C5C5C]">
          <span className="font-semibold text-[#0A0A0A]">SPV Trust (Treasury House)</span> — the bankruptcy-remote vehicle that acquires, holds, and liquidates the Subject Note. These fields drive the Certificate of Trust, True Sale Agreement, KYC Memo, and Trust Resolution.
        </p>
        <Field label="Trust Name" name="trustName" value={d.trustName} onChange={onChange} placeholder="e.g. Treasury House Trust 2026-A" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Federal EIN" name="trustEIN" value={d.trustEIN} onChange={onChange} placeholder="92-3447801" />
          <Field label="Trust Jurisdiction" name="trustJurisdiction" value={d.trustJurisdiction} onChange={onChange} />
          <Field label="Trust Execution Date" name="trustExecutionDate" type="date" value={d.trustExecutionDate} onChange={onChange} />
          <Field label="Settlor / Grantor" name="trustSettlor" value={d.trustSettlor} onChange={onChange} />
        </div>
        <Field label="Beneficiary" name="trustBeneficiary" value={d.trustBeneficiary} onChange={onChange} />
        <div className="pt-2 border-t border-[#E5E5DF]" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Managing Trustee" name="trusteeName" value={d.trusteeName} onChange={onChange} />
          <Field label="Trustee Title" name="trusteeTitle" value={d.trusteeTitle} onChange={onChange} />
          <Field label="Co-Trustee (Independent)" name="cotrusteeName" value={d.cotrusteeName} onChange={onChange} />
          <Field label="Co-Trustee Title" name="cotrusteeTitle" value={d.cotrusteeTitle} onChange={onChange} />
        </div>
        <div className="pt-2 border-t border-[#E5E5DF]" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Trust Bank" name="trustBankName" value={d.trustBankName} onChange={onChange} />
          <Field label="Trust Account #" name="trustAccountNumber" value={d.trustAccountNumber} onChange={onChange} />
          <Field label="Originator (LLC selling to Trust)" name="originatorName" value={d.originatorName} onChange={onChange} />
        </div>
        <div className="pt-2 border-t border-[#E5E5DF]" />
        <p className="text-xs text-[#5C5C5C]">Beneficial-ownership disclosure (FinCEN BOI Rule, 31 C.F.R. § 1010.380)</p>
        <div className="pt-2 border-t border-[#E5E5DF]" />
        <p className="text-xs text-[#5C5C5C]">Opinion of Counsel — signing partner on the Beale &amp; Drape LLP True-Sale Opinion (Doc T-06)</p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Counsel Firm Name" name="counselFirmName" value={d.counselFirmName} onChange={onChange} testid="counsel-firm-name" />
          <Field label="Signing Partner (Esq.)" name="counselSignatoryName" value={d.counselSignatoryName} onChange={onChange} testid="counsel-signatory-name" />
        </div>
      </div>
    );
  }

  if (step === "borrower") {
    return (
      <div className="space-y-4 fade-enter">
        <Field label="Borrower / Seller Entity" name="borrowerName" value={d.borrowerName} onChange={onChange} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="State of Formation" name="borrowerState" value={d.borrowerState} onChange={onChange} />
          <Field label="Principal City" name="borrowerCity" value={d.borrowerCity} onChange={onChange} />
        </div>
        <TextArea label="Business Address" name="borrowerAddress" value={d.borrowerAddress} onChange={onChange} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Authorized Signer Name" name="signerName" value={d.signerName} onChange={onChange} />
          <Field label="Signer Title" name="signerTitle" value={d.signerTitle} onChange={onChange} />
        </div>
        <Field label="Secretary Name (Resolution)" name="secretaryName" value={d.secretaryName} onChange={onChange} />
      </div>
    );
  }

  if (step === "lender") {
    return (
      <div className="space-y-4 fade-enter">
        <Field label="Lender / Financing Fund Name" name="lenderName" value={d.lenderName} onChange={onChange} />
        <TextArea label="Lender Address" name="lenderAddress" value={d.lenderAddress} onChange={onChange} />
        <Field label="Lender Officer Name" name="lenderOfficer" value={d.lenderOfficer} onChange={onChange} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Bank Name" name="lenderBankName" value={d.lenderBankName} onChange={onChange} />
          <Field label="Account Reference" name="lenderAccountRef" value={d.lenderAccountRef} onChange={onChange} />
          <Field label="ABA Routing #" name="lenderRouting" value={d.lenderRouting} onChange={onChange} />
          <Field label="Account #" name="lenderAccount" value={d.lenderAccount} onChange={onChange} />
        </div>
      </div>
    );
  }

  if (step === "maker") {
    return (
      <div className="space-y-4 fade-enter">
        <p className="text-sm text-[#5C5C5C]">The Maker is the obligor on the underlying $X negotiable note that secures the financing (or is being sold at a discount).</p>
        <Field label="Maker / Issuer Corporation" name="makerName" value={d.makerName} onChange={onChange} />
        <TextArea label="Maker Business Address" name="makerAddress" value={d.makerAddress} onChange={onChange} />

        <div className="pt-3 border-t border-[#E5E5DF]">
          <p className="text-sm text-[#5C5C5C] mb-3">
            <span className="font-semibold text-[#0A0A0A]">Treasury House Wordmark</span> — the name that appears in the logo lockup on the Subject Note and Discount Draft (typically the Maker/Issuer trading name).
          </p>
          <div className="grid grid-cols-1 gap-4">
            <Field label="Treasury House Name (Wordmark)" name="treasuryHouseName" value={d.treasuryHouseName} onChange={onChange} placeholder="e.g. Delta Mercantile Corp." />
            <Field label="Treasury House Tagline" name="treasuryHouseTagline" value={d.treasuryHouseTagline} onChange={onChange} placeholder="e.g. Discount & Acceptance Series A" />
          </div>
        </div>

        <div className="pt-3 border-t border-[#E5E5DF]">
          <p className="text-sm text-[#5C5C5C] mb-3">
            <span className="font-semibold text-[#0A0A0A]">Accepting Bank</span> — the commercial bank that accepts the UCC § 3-409/3-410 private negotiable instrument drawn under the Subject Note (the <span className="font-semibold">Acceptor</span>). This name prints as the Payee on the Promissory Note and the Drawee/Acceptor on the Private Negotiable Instrument.
          </p>
          <AcceptingBankPicker onPick={(cp) => {
            onChange("acceptanceBankName", cp.bank_name || cp.name || "");
            if (cp.address || cp.bank_address) onChange("acceptanceDeskAddress", cp.address || cp.bank_address);
            if (cp.contact_name) onChange("acceptanceOfficer", cp.contact_name);
            if (cp.contact_title) onChange("acceptanceOfficerTitle", cp.contact_title);
          }} />
          <Field label="Accepting Bank Name" name="acceptanceBankName" value={d.acceptanceBankName} onChange={onChange} placeholder="e.g. First Tennessee Bank, N.A." testid="field-acceptanceBankName" />
          <TextArea label="Acceptance Desk Address" name="acceptanceDeskAddress" value={d.acceptanceDeskAddress} onChange={onChange} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Acceptance Officer" name="acceptanceOfficer" value={d.acceptanceOfficer} onChange={onChange} />
            <Field label="Officer Title" name="acceptanceOfficerTitle" value={d.acceptanceOfficerTitle} onChange={onChange} />
          </div>
        </div>
      </div>
    );
  }

  if (step === "buyer") {
    return (
      <div className="space-y-4 fade-enter">
        <p className="text-sm text-[#5C5C5C]">The Buyer purchases the Note at a discount under the Note Purchase &amp; Assignment Agreement (Discount Sale package).</p>
        <Field label="Buyer Entity" name="buyerName" value={d.buyerName} onChange={onChange} />
        <TextArea label="Buyer Address" name="buyerAddress" value={d.buyerAddress} onChange={onChange} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Buyer Authorized Signer" name="buyerSigner" value={d.buyerSigner} onChange={onChange} />
          <Field label="Buyer Signer Title" name="buyerTitle" value={d.buyerTitle} onChange={onChange} />
        </div>
      </div>
    );
  }

  if (step === "proceeds") {
    return (
      <div className="space-y-4 fade-enter">
        <p className="text-sm text-[#5C5C5C]">
          <span className="font-semibold text-[#0A0A0A]">Proceeds Direction Election</span> — at closing, the SPV Trust elects to either keep the discount proceeds in the Trust account OR direct the Buyer to wire them to a named Counterparty (lender, dealership, creditor, etc.) for debt-to-debt exchange, refinancing, acquisitions, or buyer's-order purchases.
        </p>

        <div>
          <label className="field-label">Proceeds Recipient</label>
          <div className="grid grid-cols-2 gap-3" data-testid="proceeds-recipient-group">
            <button
              type="button"
              data-testid="proceeds-trustee-btn"
              className={`btn-ghost ${d.proceedsRecipient === "trustee" ? "border-[#0B4636] bg-[#E6EFEC]" : ""}`}
              onClick={() => onChange("proceedsRecipient", "trustee")}
              style={{ padding: "14px", display: "block", textAlign: "left" }}
            >
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#5C5C5C", fontWeight: 700 }}>Option A · Default</div>
              <div style={{ fontWeight: 700, marginTop: 4 }}>To SPV Trust Account</div>
              <div style={{ fontSize: 12, color: "#5C5C5C", marginTop: 4, lineHeight: 1.4 }}>Proceeds wire to {d.trustBankName}, Acct {d.trustAccountNumber}.</div>
            </button>
            <button
              type="button"
              data-testid="proceeds-counterparty-btn"
              className={`btn-ghost ${d.proceedsRecipient === "counterparty" ? "border-[#0B4636] bg-[#E6EFEC]" : ""}`}
              onClick={() => onChange("proceedsRecipient", "counterparty")}
              style={{ padding: "14px", display: "block", textAlign: "left" }}
            >
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#5C5C5C", fontWeight: 700 }}>Option B · Elect</div>
              <div style={{ fontWeight: 700, marginTop: 4 }}>To Named Counterparty</div>
              <div style={{ fontSize: 12, color: "#5C5C5C", marginTop: 4, lineHeight: 1.4 }}>Debt-to-debt exchange, refinancing, acquisitions, buyer's orders.</div>
            </button>
          </div>
        </div>

        <Field label="Purpose / Use of Proceeds" name="proceedsPurpose" value={d.proceedsPurpose} onChange={onChange} />

        {d.proceedsRecipient === "counterparty" && (
          <div className="pt-3 border-t border-[#E5E5DF] space-y-4">
            <p className="text-xs text-[#5C5C5C] uppercase tracking-[0.14em] font-bold">Counterparty Wire Instructions</p>
            <Field label="Counterparty Name" name="counterpartyName" value={d.counterpartyName} onChange={onChange} />
            <TextArea label="Counterparty Address" name="counterpartyAddress" value={d.counterpartyAddress} onChange={onChange} />
            <Field label="Counterparty Role / Relationship" name="counterpartyRole" value={d.counterpartyRole} onChange={onChange} placeholder="e.g. Senior Lender · Auto Dealership · Acquisition Escrow" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Counterparty Bank" name="counterpartyBankName" value={d.counterpartyBankName} onChange={onChange} />
              <Field label="ABA Routing #" name="counterpartyRouting" value={d.counterpartyRouting} onChange={onChange} />
              <Field label="Account #" name="counterpartyAccount" value={d.counterpartyAccount} onChange={onChange} />
            </div>
            <TextArea label="Detailed Purpose (Debt exchange / Refinancing / Buyer's order / Acquisition)" name="counterpartyPurpose" value={d.counterpartyPurpose} onChange={onChange} />
          </div>
        )}
      </div>
    );
  }

  if (step === "terms") {
    return (
      <div className="space-y-4 fade-enter">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Pledged Note Face Value ($)" name="faceValue" type="number" value={d.faceValue} onChange={onChange} />
          <Field label="Coupon Rate (% annual)" name="couponRate" type="number" value={d.couponRate} onChange={onChange} />
          <Field label="Term (days)" name="termDays" type="number" value={d.termDays} onChange={onChange} />
          <Field label="Loan Principal ($)" name="loanPrincipal" type="number" value={d.loanPrincipal} onChange={onChange} />
          <Field label="Loan Rate (% annual)" name="loanRate" type="number" value={d.loanRate} onChange={onChange} />
          <Field label="Discount Sale Price ($)" name="purchasePrice" type="number" value={d.purchasePrice} onChange={onChange} />
        </div>
      </div>
    );
  }

  if (step === "dates") {
    return (
      <div className="space-y-4 fade-enter">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Execution / Issue Date" name="executionDate" type="date" value={d.executionDate} onChange={onChange} />
          <Field label="Maturity Date" name="maturityDate" type="date" value={d.maturityDate} onChange={onChange} />
          <Field label="Sale Closing Date" name="saleClosingDate" type="date" value={d.saleClosingDate} onChange={onChange} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Governing Law (State)" name="governingLaw" value={d.governingLaw} onChange={onChange} />
          <Field label="Venue / Jurisdiction" name="jurisdiction" value={d.jurisdiction} onChange={onChange} />
        </div>
      </div>
    );
  }

  return null;
}
